// ===================================================================
// OpenGov EMC — PAPPG Chapter 6 Eligibility Engine
// Pure function checks for FEMA force account labor compliance
// ===================================================================

export interface EligibilityFlag {
  type: 'warning' | 'info';
  message: string;
  rule: string;
  code: string;
}

/**
 * Check PAPPG Chapter 6, Section III eligibility rules for a time entry.
 *
 * @param budgetStatus  'budgeted' | 'unbudgeted'
 * @param flsaStatus    'exempt' | 'non_exempt'
 * @param jobTitle      Employee job title (for supervisor check)
 * @param category      FEMA damage category: A-G, Z (admin), or ''
 * @param regHours      Regular (straight-time) hours
 * @param otHours       Overtime hours
 */
export function checkEligibility(
  budgetStatus: string,
  flsaStatus: string,
  jobTitle: string,
  category: string,
  regHours: number,
  otHours: number,
): EligibilityFlag[] {
  const flags: EligibilityFlag[] = [];
  const isBudgeted = budgetStatus === 'budgeted';
  const isExempt = flsaStatus === 'exempt';
  const cat = category.toUpperCase();
  const titleLower = (jobTitle || '').toLowerCase();

  // Rule 1: Budgeted employees on Cat B emergency protective measures — ST ineligible
  if (isBudgeted && cat === 'B' && regHours > 0) {
    flags.push({
      type: 'warning',
      code: 'budgeted_st_catB',
      message: 'Straight-time for budgeted employees on emergency work (Cat B) is generally ineligible per PAPPG Ch.6 \u00a7III.B. Only overtime is eligible. Exception: debris removal (Cat A).',
      rule: 'PAPPG v5, Ch.6, \u00a7III.B',
    });
  }

  // Rule 2: Exempt employees logging OT
  if (isExempt && otHours > 0) {
    flags.push({
      type: 'info',
      code: 'exempt_ot',
      message: 'Exempt employees are not subject to FLSA overtime. OT costs may require additional justification.',
      rule: 'PAPPG v5, Ch.6, \u00a7III.A',
    });
  }

  // Rule 3: Exempt supervisors/directors logging OT
  const supervisorKeywords = ['supervisor', 'director', 'chief', 'commissioner', 'manager'];
  if (isExempt && otHours > 0 && supervisorKeywords.some(k => titleLower.includes(k))) {
    flags.push({
      type: 'warning',
      code: 'supervisor_ot',
      message: 'Overtime for supervisors/directors is generally ineligible unless directly involved in a specific project.',
      rule: 'PAPPG v5, Ch.6, \u00a7III.C',
    });
  }

  // Rule 4: Budgeted + emergency category (not A) — any ST flagged
  // Cat A (debris) is the exception — both ST and OT eligible for budgeted employees
  // Cats C-G (permanent work) — both ST and OT eligible, so no flag
  // This rule only targets Cat B specifically (handled above), but also flag generic emergency if category isn't recognized
  // No additional rule needed here — Rule 1 covers Cat B

  // Rule 5: Admin/Management category (Z) — straight-time limited to extra hires
  if (isBudgeted && cat === 'Z' && regHours > 0) {
    flags.push({
      type: 'info',
      code: 'budgeted_st_admin',
      message: 'Budgeted employee straight-time on admin/management tasks: only eligible for extra hires. Regular budgeted staff ST is generally ineligible.',
      rule: 'PAPPG v5, Ch.6, \u00a7III.B',
    });
  }

  return flags;
}

/** Summarize a set of flags into category counts */
export function summarizeFlags(
  entries: Array<{ flags: EligibilityFlag[] }>
): { total: number; byCodes: Record<string, { count: number; label: string }> } {
  let total = 0;
  const byCodes: Record<string, { count: number; label: string }> = {};

  const labels: Record<string, string> = {
    budgeted_st_catB: 'Budgeted straight-time on Cat B',
    exempt_ot: 'Exempt employee OT',
    supervisor_ot: 'Supervisor/director OT',
    budgeted_st_admin: 'Budgeted straight-time on admin',
  };

  for (const entry of entries) {
    if (entry.flags.length > 0) total++;
    for (const f of entry.flags) {
      if (!byCodes[f.code]) byCodes[f.code] = { count: 0, label: labels[f.code] || f.code };
      byCodes[f.code].count++;
    }
  }
  return { total, byCodes };
}
